import React, { useState } from 'react';
import { Button, Dialog, Dropdown, Field } from '../../../Core';
import { withInjectedProps } from '../../../Core/InjectedPropsContext';
import { useSelector, useDispatch } from 'react-redux';
import { ListFieldSection } from '../../../Layout';
import { actions } from '../../../../Data/ReduxViewModels/WorksheetEditor';
import { WorksheetEquipmentDropdown } from '../../Core';
import { combine, pipe } from 'Lib/Hooks/Selectors';
import deepEquals from 'Lib/Object/deepEquals';
import { getById, ISieveSize, SieveSizeId } from 'Data/ViewModels/Equipment/ISieveSize';
import { hasNonEmptyValue, hasValue } from 'Lib/Any';
import { CreateWorksheetSelector, IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { IDialogProps } from 'Components/Core/Dialog/types';
import { SievingRecord } from '../types';

// Create a copy of worksheet equipment dropdown with a plain label
// while we wait to see if we want calibration status in this dialog
let StatuslessWorksheetEquipmentDropdown = (props: any) => <WorksheetEquipmentDropdown {...props} />;
(StatuslessWorksheetEquipmentDropdown as any).Label = undefined;

const createSieveSizeSelector = (createWorksheetSelector: CreateWorksheetSelector, pathToSieveSizeId: string) =>
  combine(
    [createWorksheetSelector(pathToSieveSizeId, false), createWorksheetSelector('data.Equipment.AllSieveSizes', false)],
    (id, sieves) => {
      let sieve = getById(sieves, id);
      if (hasValue(id) && !hasNonEmptyValue(sieve))
        throw new Error(`Could not find sieve size with id ${id} in data.Equipment.AllSieveSizes`);
      return sieve;
    }
  );

let PortionSievingAddSieveDialog: React.FC<IDialogProps & IWorksheetComponent> = ({
  onClose = undefined,
  visible = undefined,
  namePrefix,
  createWorksheetSelector = undefined,
  ...otherProps
}) => {
  const dispatch = useDispatch();

  const [sieveSizeId, setSieveSizeId] = useState<SieveSizeId>();
  const [sieve, setSieve] = useState();

  const portionPath = namePrefix!.slice(0, namePrefix!.lastIndexOf('.'));

  const existingRecordCount = useSelector(pipe(createWorksheetSelector!('Records'), (r) => r?.length));

  const availableSieveSizes = useSelector(
    combine(
      [
        createSieveSizeSelector(createWorksheetSelector!, `${portionPath}.UpperLimitSieveSizeId`),
        createSieveSizeSelector(createWorksheetSelector!, `${portionPath}.LowerLimitSieveSizeId`),
        createSieveSizeSelector(createWorksheetSelector!, `${portionPath}.Washing.WashingSieveSizeId`),
        pipe(
          createWorksheetSelector!('Records'),
          (r: SievingRecord[]) => r?.filter((r) => hasNonEmptyValue(r.SieveSize)).map((r) => r.SieveSize!.Id) ?? []
        ),
        createWorksheetSelector!('data.Equipment.AllSieveSizes', false),
      ],
      (
        upperLimitSieveSize: ISieveSize | undefined,
        lowerLimitSieveSize: ISieveSize | undefined,
        washingSieveSize: ISieveSize | undefined,
        recordSieveSizeIds: SieveSizeId[],
        allSieveSizes: ISieveSize[]
      ) => {
        if (!allSieveSizes || allSieveSizes.some((s) => !s.Size || s.Size.uom !== allSieveSizes[0].Size!.uom))
          throw new Error(
            'Cannot compare sieves sizes in data.Equipment.AllSieveSizes. Sizes are missing or are defined with inconsistent units.'
          );

        return allSieveSizes
          .filter((s) => !recordSieveSizeIds?.includes(s.Id))
          .filter(
            (s) =>
              !lowerLimitSieveSize ||
              Number(s.Size!.value).valueOf() >= Number(lowerLimitSieveSize.Size!.value).valueOf()
          )
          .filter(
            (s) =>
              !upperLimitSieveSize ||
              Number(s.Size!.value).valueOf() < Number(upperLimitSieveSize.Size!.value).valueOf()
          )
          .filter(
            (s) =>
              !washingSieveSize || Number(s.Size!.value).valueOf() >= Number(washingSieveSize.Size!.value).valueOf()
          );
      }
    ),
    deepEquals
  );

  const selectedSieveSize = availableSieveSizes?.find((s) => s.Id === sieveSizeId);
  const sieveSizeOptions = availableSieveSizes?.map((s) => ({ value: s.Id, label: s.Caption }));

  const addSieve = () => {
    dispatch(
      actions.addArrayEntry({
        name: `${namePrefix}.Records`,
        index: existingRecordCount,
        data: {
          SieveSizeId: sieveSizeId,
          SieveSize: selectedSieveSize,
          Sieve: sieve,
        },
        sync: true,
      })
    );
    cleanupAndClose();
  };

  const cleanupAndClose = () => {
    setSieve(undefined);
    setSieveSizeId(undefined);
    onClose!();
  };

  return (
    <Dialog
      {...otherProps}
      title="Add Sieve to Nest"
      width="400px"
      visible={visible}
      onClose={cleanupAndClose}
      buttons={[
        <Button primary key="1" onClick={addSieve} disabled={!sieveSizeId}>
          Add
        </Button>,
        <Button key="0" onClick={onClose as any}>
          Cancel
        </Button>,
      ]}
    >
      <ListFieldSection>
        <Field
          input={<Dropdown />}
          label="Sieve Size"
          value={sieveSizeId}
          onChange={(e: any) => {
            setSieveSizeId(e.data.value);
            setSieve(undefined);
          }}
          options={sieveSizeOptions}
          simpleValue
        />
        <Field
          input={<StatuslessWorksheetEquipmentDropdown />}
          label="Sieve ID"
          disabled={!selectedSieveSize}
          value={sieve}
          equipmentType="sieve"
          queryParams={
            selectedSieveSize ? { SieveSize: `${selectedSieveSize.Size!.value} ${selectedSieveSize.Size!.uom}` } : null
          }
          onChange={(e: any) => setSieve(e.data.value)}
        />
      </ListFieldSection>
    </Dialog>
  );
};

PortionSievingAddSieveDialog = withInjectedProps(PortionSievingAddSieveDialog);

export { PortionSievingAddSieveDialog };
